/**
 * Le preferenze audio, condivise fra la barra e la schermata evento.
 *
 * Sono una preferenza del **dispositivo**, non dello stato condiviso: il tablet
 * del banco 2 può avere il volume alzato e quello del banco 1 muto, e nessuno
 * dei due deve sapere niente dell'altro. Per questo vivono in `localStorage` e
 * non nel database, e per questo non passano dal reducer dell'evento.
 *
 * Lo stato è tenuto in un piccolo archivio esterno invece che in un contesto
 * React: chi cambia il volume dalla barra e chi suona dal banco sono due
 * componenti lontani nell'albero, e un contesto in più da avvolgere attorno a
 * tutta l'applicazione per una preferenza sola sarebbe più impalcatura che
 * sostanza.
 */
import { useSyncExternalStore } from 'react';
import {
  DEFAULT_AUDIO_SETTINGS,
  loadAudioSettings,
  saveAudioSettings,
  type AudioSettings,
} from './sounds';

let current: AudioSettings | null = null;
const listeners = new Set<() => void>();

/**
 * Le preferenze correnti, lette da `localStorage` alla prima richiesta.
 *
 * La lettura è pigra di proposito: `localStorage` non esiste durante il rendering
 * sul server né nei test senza DOM, e leggerlo al caricamento del modulo
 * significherebbe far fallire l'import di questo file invece della sola lettura.
 */
function read(): AudioSettings {
  if (current === null) current = loadAudioSettings();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setAudioSettings(next: AudioSettings): void {
  current = next;
  saveAudioSettings(next);
  for (const listener of listeners) listener();
}

export function useAudioSettings(): AudioSettings {
  // Il terzo argomento è la lettura per il rendering sul server; qui non serve,
  // ma `useSyncExternalStore` lo vuole e `read` è già sicuro senza DOM.
  return useSyncExternalStore(subscribe, read, read);
}

/** Solo per i test: riporta l'archivio allo stato iniziale. */
export function resetAudioSettingsForTests(initial: AudioSettings = DEFAULT_AUDIO_SETTINGS): void {
  current = initial;
  listeners.clear();
}
